<template>
    <div :alvqa="alvqa">
        <div class="subtitle-1 font-weight-medium d-flex align-center">
            {{ $t('lang_PUBLISHING_USAGE_CAPTION_STATIONS') }}
            <span v-if="counts" class="ml-3 body-2 subtitleGray--text" :alvqa="alvqa + '-counts'">
                {{ $t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_COUNTS', {
                    namesOnList: $n(counts.namesOnList), names: $n(counts.names),
                    rowsOnList: $n(counts.rowsOnList), rows: $n(counts.rows)
                }) }}
            </span>
        </div>
        <v-alert v-if="unlisted > 0" color="standardBrownIcon" icon="warning_amber" dense border="left" colored-border class="mt-2 mb-2" :alvqa="alvqa + '-unlisted-hint'">
            {{ $t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_UNLISTED_HINT') }}
        </v-alert>
        <v-data-table
            :headers="headers"
            :items="stations"
            :loading="loading"
            :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
            :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
            hide-default-footer
            :items-per-page="-1"
            dense
            class="usage-nested-table"
            style="background-color: var(--v-expandPanelBackground-base);"
        >
            <template v-slot:[`item.stationName`]="{ item }">
                <span :alvqa="alvqa + '-name-' + item.stationName">{{ item.stationName }}</span>
            </template>
            <template v-slot:[`item.rows`]="{ item }">{{ $n(item.rows) }}</template>
            <template v-slot:[`item.onList`]="{ item }">
                <BaseTooltip>
                    <template v-slot:content>
                        <v-icon small :color="item.onList ? 'buttonNewEntity' : 'error'">{{ item.onList ? 'check_circle' : 'help_outline' }}</v-icon>
                    </template>
                    <template v-slot:text>{{ $t(resolutionText(item)) }}</template>
                </BaseTooltip>
            </template>
            <template v-slot:[`item.stationCode`]="{ item }">
                <v-autocomplete
                    :value="item.stationCode"
                    :items="stationItems"
                    :placeholder="$t('lang_GENERIC_CAPTION_PLEASE_SELECT')"
                    :no-data-text="$t('lang_GENERAL_CAPTION_NO_DATA_AVAILABLE')"
                    dense
                    hide-details
                    :clearable="!!item.aliasId"
                    :disabled="saving === item.stationName"
                    :loading="saving === item.stationName"
                    :alvqa="alvqa + '-autocomplete-' + item.stationName"
                    @change="code => assign(item, code)"
                >
                    <template v-slot:prepend-item v-if="item.candidates && item.candidates.length">
                        <v-subheader>{{ $t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_CANDIDATES') }}</v-subheader>
                        <v-list-item v-for="code in item.candidates" :key="'c' + code" @click="assign(item, code)">
                            <v-list-item-content><v-list-item-title>{{ stationLabel(code) }}</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-divider />
                    </template>
                </v-autocomplete>
            </template>
            <template v-slot:[`item.source`]="{ item }">
                <span class="caption subtitleGray--text">{{ item.aliasId ? $t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_ALIAS') : (item.resolution ? $t(resolutionText(item)) : '') }}</span>
            </template>
        </v-data-table>
        <div v-if="changed && dataset.dataType === 'broadcast_schedule'" class="mt-2 body-2" :alvqa="alvqa + '-compare-again-hint'">
            {{ $t('lang_PUBLISHING_USAGE_CAPTION_COMPARE_AGAIN_HINT') }}
        </div>
    </div>
</template>

<script>
import BaseTooltip from '@/components/_base/BaseTooltip'
import usageApi from '@/api/usageApi'
import { usageErrorText } from '@/store/modules/2_publishing/usage'

/** The station names of a dataset and the GEMA station each one counts as. Choosing a station saves an alias for the
 *  name (for the whole account); the next comparison uses it. */
export default {
    name: 'UsageStationsPanel',
    components: { BaseTooltip },
    props: {
        dataset: { type: Object, required: true }
    },
    data() {
        return {
            alvqa: 'publishing-usage-stations',
            loading: false,
            stations: [],
            counts: null,
            saving: null,
            changed: false
        }
    },
    computed: {
        headers() {
            return [
                { text: this.$t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_NAME'), value: 'stationName', sortable: false },
                { text: this.$t('lang_PUBLISHING_INCOME_CAPTION_RECORDS'), value: 'rows', align: 'right', width: '90px', sortable: false },
                { text: '', value: 'onList', width: '40px', sortable: false },
                { text: this.$t('lang_PUBLISHING_USAGE_CAPTION_STATIONS_GEMA_STATION'), value: 'stationCode', width: '360px', sortable: false },
                { text: '', value: 'source', width: '160px', sortable: false }
            ]
        },
        stationItems() {
            return this.$store.getters.getUsageStationList.map(s => ({ value: s.code, text: s.code + ' — ' + s.name }))
        },
        unlisted() { return this.stations.filter(s => !s.onList).length }
    },
    methods: {
        // Several GEMA stations share the name: the API lists their codes as candidates
        stationLabel(code) {
            const station = this.$store.getters.getUsageStationList.find(s => s.code === code)
            return station ? code + ' — ' + station.name : code
        },
        resolutionText(item) {
            if (item.aliasId) return 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_ALIAS'
            return {
                name: 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_NAME',
                code: 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_CODE',
                file: 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_FILE',
                customer_alias: 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_BY_ALIAS'
            }[item.resolution] || 'lang_PUBLISHING_USAGE_CAPTION_STATIONS_NOT_ON_LIST'
        },
        async load() {
            this.loading = true
            const result = await usageApi.datasetStations(this.dataset.id)
            this.loading = false
            if (!result.ok) return this.$store.commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            this.stations = result.data.stations
            this.counts = result.data.counts
        },
        async assign(item, code) {
            this.saving = item.stationName
            let result
            if (!code && item.aliasId) result = await usageApi.deleteStationAlias(item.aliasId)
            else if (code && item.aliasId) result = await usageApi.updateStationAlias(item.aliasId, { stationName: item.stationName, stationCode: code })
            else if (code) result = await usageApi.createStationAlias({ stationName: item.stationName, stationCode: code })
            this.saving = null
            if (result && !result.ok) {
                this.$store.commit('setSnackbar', { type: 'error', msg: usageErrorText(result.error) })
            } else if (result) {
                this.changed = true
                this.$store.commit('setSnackbar', { type: 'success', msg: this.$t('lang_GENERIC_SNACKBAR_SAVING_SUCCESSFUL') })
            }
            this.load()
        }
    },
    created() {
        this.load()
    }
}
</script>
